import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const Hero: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const slides = [
    {
      title: "Somos mais que uma empresa de T.I.",
      subtitle: "Entendemos a informação como algo complexo e usamos conhecimentos de diversas áreas para buscar soluções inteligentes",
      background: "/images/index-presentation-2.jpg"
    },
    {
      title: "Desenvolvemos soluções tecnológicas",
      subtitle: "Mais que linhas de códigos, transformamos ideias em novos Negócios",
      background: "/images/index-presentation-1.jpg"
    },
    {
      title: "Nós focamos nos usuários",
      subtitle: "Desenvolvemos soluções personalizadas para atender as diferentes necessidades das pessoas em contextos específicos",
      background: "/images/index-presentation-3.jpg"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [slides.length]);

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="hero" className="relative h-screen flex items-center justify-center overflow-hidden">
      {/* Background Images */}
      {slides.map((slide, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === currentSlide ? 'opacity-100' : 'opacity-0'
          }`}
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${slide.background})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
      ))}

      {/* Content */}
      <div ref={ref} className="relative z-10 container-custom text-center text-white">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold font-heading mb-6 text-shadow-lg">
            {slides[currentSlide].title}
          </h1>
          <motion.p
            key={currentSlide}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-xl md:text-2xl mb-8 max-w-4xl mx-auto text-shadow"
          >
            {slides[currentSlide].subtitle}
          </motion.p>
          <motion.button
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            onClick={scrollToContact}
            className="btn-secondary text-white border-white hover:bg-white hover:text-primary-600 text-lg px-8 py-4"
          >
            Fale Conosco
          </motion.button>
        </motion.div>
      </div>

      {/* Slide Indicators */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-3 z-10">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${
              index === currentSlide ? 'bg-white' : 'bg-white/50'
            }`}
          />
        ))}
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 0.6 }}
        className="absolute bottom-8 right-8 text-white"
      >
        <div className="flex flex-col items-center">
          <span className="text-sm mb-2">Scroll</span>
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="w-0.5 h-6 bg-white rounded-full"
          />
        </div>
      </motion.div>

      {/* Diagonal Shape */}
      <div className="absolute bottom-0 left-0 right-0 h-24">
        <svg className="w-full h-full" viewBox="0 0 1920 167.98" preserveAspectRatio="none">
          <polygon 
            points="1920 167.98 0 167.98 0 0 1920 167.98" 
            className="fill-white"
          />
        </svg>
      </div>
    </section>
  );
};

export default Hero;
